import { lastValueFrom, Observable } from 'rxjs';
import { VendorService } from '../services/vendor/vendor.service';

/**
 * Vendor typeahead search result
 */
export interface VendorTypeaheadResult {
  options: any[]; // Formatted vendor options for autocomplete
  addresses: Map<string, any[]>; // Map of vendor_id -> addresses
  selectedVendor?: any; // Currently selected vendor object (if any)
}

/**
 * Vendor typeahead utility for handling vendor search and data formatting
 */
export class VendorTypeaheadUtils {
  /**
   * Normalize vendor ID from various formats (object, string, number)
   */
  static normalizeVendorId(value: any): string | number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'object') {
      if (value.value !== undefined && value.value !== null && value.value !== '') {
        return value.value;
      }
      if (value.id !== undefined && value.id !== null && value.id !== '') {
        return value.id;
      }
    }
    return value;
  }

  /**
   * Format vendor data into autocomplete option format
   */
  static formatVendorOption(vendor: any): any {
    return {
      value: vendor.id,
      label: vendor.company_name,
      vendor_code: vendor.vendor_code,
      ...vendor
    };
  }

  /**
   * Extract and format addresses from vendor tax_information
   */
  static extractVendorAddresses(vendor: any): any[] {
    const addresses: any[] = [];
    if (Array.isArray(vendor.tax_information)) {
      vendor.tax_information.forEach((information: any) => {
        if (information?.address) {
          addresses.push({
            value: information.id,
            label: information.address,
            ...information,
          });
        }
      });
    }
    return addresses;
  }

  /**
   * Search vendors and return formatted result
   */
  static async searchVendors(
    query: string,
    vendorService: VendorService,
    currentVendorId?: string | number | null,
    existingOptions: any[] = [],
    limit: number = 50
  ): Promise<VendorTypeaheadResult> {
    const normalizedQuery = (query || '').trim();
    
    // If query is empty, return empty result
    if (!normalizedQuery) {
      return {
        options: [],
        addresses: new Map(),
        selectedVendor: existingOptions.find(opt => 
          currentVendorId && String(opt.value) === String(currentVendorId)
        )
      };
    }

    try {
      // Make API call with search query
      const response = await lastValueFrom(
        vendorService.getAllVendors({
          type: 'non-draft',
          company_name: normalizedQuery,
          limit
        })
      );

      const vendors = response?.data?.vendors || [];
      const vendorOptions = vendors.map((vendor: any) => this.formatVendorOption(vendor));

      // Preserve currently selected vendor in options
      const existingVendors = existingOptions.filter(opt => 
        currentVendorId && String(opt.value) === String(currentVendorId)
      );
      
      // Merge search results with existing vendors
      const mergedOptions = [...existingVendors, ...vendorOptions];
      
      // Remove duplicates based on value
      const uniqueOptions = Array.from(
        new Map(mergedOptions.map(opt => [String(opt.value), opt])).values()
      );

      // Build address map
      const addressMap = new Map<string, any[]>();
      vendorOptions.forEach((vendorOption: any) => {
        const addresses = this.extractVendorAddresses(vendorOption);
        if (addresses.length > 0) {
          addressMap.set(String(vendorOption.value ?? vendorOption.id), addresses);
        }
      });

      // Find selected vendor object
      const selectedVendor = currentVendorId 
        ? uniqueOptions.find(opt => String(opt.value) === String(currentVendorId))
        : undefined;

      return {
        options: uniqueOptions,
        addresses: addressMap,
        selectedVendor
      };
    } catch (error) {
      console.error('Error searching vendors:', error);
      return {
        options: existingOptions,
        addresses: new Map(),
        selectedVendor: existingOptions.find(opt => 
          currentVendorId && String(opt.value) === String(currentVendorId)
        )
      };
    }
  }

  /**
   * Load single vendor by ID and return formatted result
   */
  static async loadVendorById(
    vendorId: string | number,
    vendorService: VendorService
  ): Promise<VendorTypeaheadResult> {
    try {
      const vendorResponse = await lastValueFrom(
        vendorService.getSingleVendor(String(vendorId))
      );
      
      const vendor = vendorResponse?.data?.vendor || vendorResponse?.data;
      if (!vendor) {
        return {
          options: [],
          addresses: new Map()
        };
      }

      const vendorOption = this.formatVendorOption(vendor);
      const addresses = this.extractVendorAddresses(vendor);
      
      const addressMap = new Map<string, any[]>();
      if (addresses.length > 0) {
        addressMap.set(String(vendorId), addresses);
      }

      return {
        options: [vendorOption],
        addresses: addressMap,
        selectedVendor: vendorOption
      };
    } catch (error) {
      console.error('Error loading vendor:', error);
      return {
        options: [],
        addresses: new Map()
      };
    }
  }

  /**
   * Ensure vendor object is properly formatted (has label property)
   * If vendor is just an ID, loads it from API
   */
  static async ensureVendorObject(
    vendorValue: any,
    vendorService: VendorService,
    existingOptions: any[] = []
  ): Promise<any | null> {
    const normalizedId = this.normalizeVendorId(vendorValue);
    if (!normalizedId) {
      return null;
    }

    // Check if value is already a vendor object with label
    const isVendorObject = vendorValue && typeof vendorValue === 'object' && vendorValue.label;
    
    if (isVendorObject) {
      // Ensure it has all required properties
      if (!vendorValue.value && normalizedId) {
        vendorValue.value = normalizedId;
      }
      if (!vendorValue.label && vendorValue.company_name) {
        vendorValue.label = vendorValue.company_name;
      }
      return vendorValue;
    }

    // Try to find in existing options
    const existingOption = existingOptions.find(opt => String(opt.value) === String(normalizedId));
    if (existingOption) {
      return existingOption;
    }

    // Load from API
    const result = await this.loadVendorById(normalizedId, vendorService);
    return result.selectedVendor || null;
  }
}

